import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import type {Breakpoint, Theme} from '@mui/material/styles';
import type {CmPictureType} from '../types';
import {getAiGeneratedImageHintText, isAiGeneratedImage} from '../../utility/aiGeneratedImage';
import {getLengthLimit} from '../../utility/lengthLimits';
import {usePreviewLimitCheck} from '../preview/usePreviewLimitCheck';

const AI_GENERATED_IMAGE_LABEL_CHAR_LIMIT = getLengthLimit('CCOAiGeneratedImageLabel.text');

const LABEL_LINE_HEIGHT = 13;
const LABEL_CAP_HEIGHT = 7;
const LEADING_TRIM = `-${(LABEL_LINE_HEIGHT - LABEL_CAP_HEIGHT) / 2}px`;

export type AiGeneratedImageLabelVariant = 'productCard' | 'stage' | 'textOverlayTile' | 'tile';
type LabelPlacement = 'top-right' | 'bottom-right';
type ResponsiveLabelPlacement = LabelPlacement | Partial<Record<Breakpoint, LabelPlacement>>;
type LabelMaxWidth = string | Partial<Record<Breakpoint, string>>;

interface LabelLayout {
    inset: number;
    maxWidth: LabelMaxWidth;
    placement: ResponsiveLabelPlacement;
}

const LABEL_LAYOUTS: Record<AiGeneratedImageLabelVariant, LabelLayout> = {
    productCard: {inset: 16, maxWidth: getLabelMaxWidth(16), placement: 'bottom-right'},
    stage: {
        inset: 24,
        maxWidth: {xs: getLabelMaxWidth(24), md: 'calc(50% - 72px)'},
        placement: {xs: 'bottom-right', md: 'top-right'},
    },
    textOverlayTile: {inset: 24, maxWidth: getLabelMaxWidth(24), placement: 'top-right'},
    tile: {inset: 24, maxWidth: getLabelMaxWidth(24), placement: 'bottom-right'},
};

function getLabelMaxWidth(inset: number) {
    return `calc(100% - 2 * ${inset}px)`;
}

function getPlacementStyle(placement: ResponsiveLabelPlacement, inset: number) {
    const offset = `${inset}px`;
    const getEdgeOffset = (edge: LabelPlacement) => typeof placement === 'string' ?
        (placement === edge ? offset : 'auto') :
        Object.fromEntries(Object.entries(placement).map(([breakpoint, corner]) =>
            [breakpoint, corner === edge ? offset : 'auto']));
    return {bottom: getEdgeOffset('bottom-right'), right: offset, top: getEdgeOffset('top-right')};
}

export interface AiGeneratedImageLabel {
    imageAlt: string;
    text: string;
}

interface Props {
    label: AiGeneratedImageLabel;
    variant: AiGeneratedImageLabelVariant;
}

export function useAiGeneratedImageLabel(picture: CmPictureType | null | undefined,
                                         imageAlt = ''): AiGeneratedImageLabel | undefined {
    const {t} = useTranslation();
    const isLabelShown = isAiGeneratedImage(picture);
    const text = getAiGeneratedImageHintText(picture) ?? t('aiGeneratedImageHint')!;

    usePreviewLimitCheck({
        id: picture?.id,
        component: 'CCOAiGeneratedImageLabel',
        field: 'aiGeneratedImageHintText',
        value: isLabelShown ? text : '',
        limit: AI_GENERATED_IMAGE_LABEL_CHAR_LIMIT,
    });

    if (!isLabelShown) {
        return undefined;
    }
    return {
        imageAlt: imageAlt && `${imageAlt}, ${text}`,
        text,
    };
}

function CCOAiGeneratedImageLabel(props: Props) {
    const layout = LABEL_LAYOUTS[props.variant];
    const labelStyle = {
        ...getPlacementStyle(layout.placement, layout.inset),
        backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.7),
        boxSizing: 'border-box',
        color: 'common.white',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '0.275px',
        lineHeight: `${LABEL_LINE_HEIGHT}px`,
        maxWidth: layout.maxWidth,
        overflowWrap: 'anywhere',
        p: 2,
        position: 'absolute',
        zIndex: 1,
        '&::before, &::after': {
            content: '""',
            display: 'table',
        },
        '&::before': {
            marginBottom: LEADING_TRIM,
        },
        '&::after': {
            marginTop: LEADING_TRIM,
        },
    } as const;

    return <Typography component='span'
                       aria-hidden='true'
                       data-cypress='aiGeneratedImageLabel'
                       sx={labelStyle}
                       variant='caption'>
        {props.label.text}
    </Typography>;
}

export default CCOAiGeneratedImageLabel;
